import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import tristeAnimation from "../assets/triste.json";
import atencaoAnimation from "../assets/atencao.json";
import phonecallAnimation from "../assets/phonecall.json";

/* =================== Helpers de data =================== */
function parseLocalDate(str) {
  if (!str) return null;
  const [ano, mes, dia] = str.split("-").map(Number);
  if (!ano || !mes || !dia) return null;
  return new Date(ano, mes - 1, dia);
}
function diasRestantesNoMes() {
  const hoje = new Date();
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
  return ultimoDia.getDate() - hoje.getDate();
}
function calcularTaxaConversao(leads, usuarioId) {
  if (!Array.isArray(leads)) return 0;
  const meusLeads = leads.filter(
    (l) => String(l.usuario_criador_id) === String(usuarioId)
  );
  if (meusLeads.length === 0) return 0;
  const ganhos = meusLeads.filter((l) => {
    const status = (l.status_negociacao || "").trim().toLowerCase();
    return ["ganho", "fechado", "vendido", "concluído"].includes(status);
  });
  return (ganhos.length / meusLeads.length) * 100;
}
function faltamParaMeta(leads, usuarioId, meta = 30) {
  const meusLeads = leads.filter(
    (l) => String(l.usuario_criador_id) === String(usuarioId)
  );
  const ganhos = meusLeads.filter((l) => {
    const status = (l.status_negociacao || "").trim().toLowerCase();
    return ["ganho", "fechado", "vendido", "concluído"].includes(status);
  });
  if (meusLeads.length === 0) return 0;
  const totalLeads = meusLeads.length;
  const metaGanhos = Math.ceil((meta / 100) * totalLeads);
  return Math.max(metaGanhos - ganhos.length, 0);
}

/* =================== Filtros de tarefas =================== */
function filtrarLeadsComTarefaAtrasada({ tarefas, leads, usuarioId }) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return leads.filter((lead) => {
    const statusNeg = (lead.status_negociacao || "").trim().toLowerCase();
    if (["perdido", "migrou concorrência", "fechado"].includes(statusNeg))
      return false;
    if (lead.usuario_criador_id !== usuarioId) return false;

    const tarefasDaLead = tarefas
      .filter((t) => t.lead_id === lead.id)
      .sort((a, b) => (b.id || 0) - (a.id || 0));

    if (tarefasDaLead.length === 0) return false;
    const ultima = tarefasDaLead[0];
    const venc = parseLocalDate((ultima.data_vencimento || "").split("T")[0]);
    if (ultima.status === "Concluído" || !venc || venc >= hoje) return false;
    return true;
  });
}
function filtrarLeadsComTarefaHoje({ tarefas, leads, usuarioId }) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return leads.filter((lead) => {
    const statusNeg = (lead.status_negociacao || "").trim().toLowerCase();
    if (["perdido", "migrou concorrência", "fechado"].includes(statusNeg))
      return false;
    if (lead.usuario_criador_id !== usuarioId) return false;

    const tarefasDaLead = tarefas
      .filter((t) => t.lead_id === lead.id)
      .sort((a, b) => (b.id || 0) - (a.id || 0));

    if (tarefasDaLead.length === 0) return false;
    const ultima = tarefasDaLead[0];
    const venc = parseLocalDate((ultima.data_vencimento || "").split("T")[0]);
    return (
      ultima.status !== "Concluído" && venc && venc.getTime() === hoje.getTime()
    );
  });
}

/* =================== Cooldown =================== */
const now = () => Date.now();
const getNum = (k, def = 0) => {
  const v = Number(localStorage.getItem(k));
  return Number.isFinite(v) ? v : def;
};
const setNum = (k, v) => localStorage.setItem(k, String(v));
function canShow(key, cooldownMs) {
  const silencedUntil = getNum("nt_silenced_until", 0);
  if (now() < silencedUntil) return false;
  const last = getNum(key, 0);
  return now() - last > cooldownMs;
}
function markShown(key) {
  setNum(key, now());
}

/* =================== Componente =================== */
export default function NotificacaoTarefa({ tarefas, leads }) {
  const tarefasArray = Array.isArray(tarefas) ? tarefas : tarefas?.data || [];

  const [mostrarAtrasadas, setMostrarAtrasadas] = useState(false);
  const [mostrarHoje, setMostrarHoje] = useState(false);
  const [mostrarTermometro, setMostrarTermometro] = useState(false);
  const [qtdAtrasadas, setQtdAtrasadas] = useState(0);
  const [qtdHoje, setQtdHoje] = useState(0);
  const [taxaConversao, setTaxaConversao] = useState(100);

  // Áudio
  const [audioReady, setAudioReady] = useState(false);
  const audioAtrasadasRef = useRef(null);
  const audioHojeRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Refs de estado anterior / timers
  const didInitRef = useRef(false);
  const prevAtrasadasRef = useRef(0);
  const prevHojeRef = useRef(0);
  const prevAbaixo30Ref = useRef(false);

  const intervaloRef = useRef(null);
  const timerHojeRef = useRef(null);
  const timerTermometroRef = useRef(null);

  const usuario = JSON.parse(localStorage.getItem("usuarioLogado") || "{}");
  const usuarioId = usuario?.id;

  /* ====== Desbloqueio de áudio após 1ª interação ====== */
  useEffect(() => {
    const onFirstInteract = async () => {
      try {
        // WebAudio (Safari/iOS costuma exigir resume)
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
          if (audioCtxRef.current.state === "suspended") {
            await audioCtxRef.current.resume();
          }
        }
        // desmutar as tags <audio>
        if (audioAtrasadasRef.current) audioAtrasadasRef.current.muted = false;
        if (audioHojeRef.current) audioHojeRef.current.muted = false;
        setAudioReady(true);
      } catch (e) {
        console.warn("Falha ao preparar áudio:", e);
      } finally {
        window.removeEventListener("click", onFirstInteract);
        window.removeEventListener("keydown", onFirstInteract);
        window.removeEventListener("touchstart", onFirstInteract);
      }
    };

    // começar mudo para respeitar autoplay policy
    if (audioAtrasadasRef.current) audioAtrasadasRef.current.muted = true;
    if (audioHojeRef.current) audioHojeRef.current.muted = true;

    window.addEventListener("click", onFirstInteract);
    window.addEventListener("keydown", onFirstInteract);
    window.addEventListener("touchstart", onFirstInteract, { passive: true });

    return () => {
      window.removeEventListener("click", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
      window.removeEventListener("touchstart", onFirstInteract);
    };
  }, []);

  const safePlay = async (ref) => {
    try {
      if (!ref?.current) return;
      if (!audioReady) return; // ainda sem interação do usuário
      // reinicia do início cada vez
      ref.current.currentTime = 0;
      await ref.current.play();
    } catch (err) {
      // não explode a UI — só loga
      console.warn("play() bloqueado ou falhou:", err?.message || err);
    }
  };

  const checarEExibir = () => {
    // Calcula listas e métricas
    const leadsAtrasadas = filtrarLeadsComTarefaAtrasada({
      tarefas: tarefasArray,
      leads,
      usuarioId,
    });
    const leadsHoje = filtrarLeadsComTarefaHoje({
      tarefas: tarefasArray,
      leads,
      usuarioId,
    });
    const taxa = calcularTaxaConversao(leads, usuarioId);
    const abaixo30 = taxa < 30;

    setQtdAtrasadas(leadsAtrasadas.length);
    setQtdHoje(leadsHoje.length);
    setTaxaConversao(taxa);

    // No primeiro ciclo apenas salva baseline
    if (!didInitRef.current) {
      didInitRef.current = true;
      prevAtrasadasRef.current = leadsAtrasadas.length;
      prevHojeRef.current = leadsHoje.length;
      prevAbaixo30Ref.current = abaixo30;
      return;
    }

    // ======= Regras com transição + cooldown =======
    // 1) Atrasadas
    if (
      leadsAtrasadas.length > prevAtrasadasRef.current &&
      leadsAtrasadas.length > 0 &&
      canShow("nt_last_atrasadas", 30 * 60 * 1000)
    ) {
      setMostrarAtrasadas(true);
      safePlay(audioAtrasadasRef);
      setTimeout(() => setMostrarAtrasadas(false), 7000);
      markShown("nt_last_atrasadas");
    }

    // 2) Hoje
    if (
      leadsHoje.length > prevHojeRef.current &&
      leadsHoje.length > 0 &&
      canShow("nt_last_hoje", 30 * 60 * 1000)
    ) {
      if (timerHojeRef.current) clearTimeout(timerHojeRef.current);
      timerHojeRef.current = setTimeout(() => {
        setMostrarHoje(true);
        safePlay(audioHojeRef);
        setTimeout(() => setMostrarHoje(false), 7000);
        markShown("nt_last_hoje");
      }, 1200);
    }

    // 3) Termômetro (cruzou de >=30 para <30)
    if (
      abaixo30 &&
      !prevAbaixo30Ref.current &&
      canShow("nt_last_termometro", 2 * 60 * 60 * 1000)
    ) {
      setMostrarTermometro(true);
      if (timerTermometroRef.current) clearTimeout(timerTermometroRef.current);
      timerTermometroRef.current = setTimeout(
        () => setMostrarTermometro(false),
        7000
      );
      markShown("nt_last_termometro");
    } else if (!abaixo30) {
      setMostrarTermometro(false);
    }

    // Atualiza baseline
    prevAtrasadasRef.current = leadsAtrasadas.length;
    prevHojeRef.current = leadsHoje.length;
    prevAbaixo30Ref.current = abaixo30;
  };

  // Debounce quando dados mudam
  useEffect(() => {
    const t = setTimeout(checarEExibir, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarefasArray, leads]);

  // Checagem periódica
  useEffect(() => {
    intervaloRef.current = setInterval(checarEExibir, 10 * 60 * 1000);
    return () => {
      if (intervaloRef.current) clearInterval(intervaloRef.current);
      if (timerHojeRef.current) clearTimeout(timerHojeRef.current);
      if (timerTermometroRef.current) clearTimeout(timerTermometroRef.current);
      // pausa áudios no unmount
      if (audioAtrasadasRef.current) {
        audioAtrasadasRef.current.pause();
        audioAtrasadasRef.current.currentTime = 0;
      }
      if (audioHojeRef.current) {
        audioHojeRef.current.pause();
        audioHojeRef.current.currentTime = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const silenciar2h = () => {
    setNum("nt_silenced_until", now() + 2 * 60 * 60 * 1000);
    setMostrarAtrasadas(false);
    setMostrarHoje(false);
    setMostrarTermometro(false);
  };

  return (
    <>
      {/* Áudios pré-carregados, começam mutados e são “desbloqueados” na 1ª interação */}
      <audio ref={audioAtrasadasRef} preload="auto" src="/alerta.mp3" muted />
      <audio ref={audioHojeRef} preload="auto" src="/alertaII.mp3" muted />

      <AnimatePresence>
        {/* --- TERMÔMETRO --- */}
        {mostrarTermometro && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="fixed bottom-5 right-5 bg-blue-100 shadow-2xl rounded-xl border border-blue-400 z-[9999] flex items-center space-x-4 p-4 max-w-sm"
          >
            <div className="w-16 h-16">
              <Lottie animationData={phonecallAnimation} loop autoplay />
            </div>
            <div>
              <h2 className="text-lg font-bold text-blue-700">
                🚀 Sua meta está próxima!
              </h2>
              <p className="text-sm text-blue-700">
                Conversão atual:{" "}
                <span className="font-bold text-blue-900">
                  {taxaConversao.toFixed(1)}%
                </span>
                .<br />
                Faltam <span className="font-bold">
                  {diasRestantesNoMes()}
                </span>{" "}
                dia(s) para o fim do mês.
                <br />
                Só mais{" "}
                <span className="font-bold">
                  {faltamParaMeta(leads, usuarioId, 30)}
                </span>{" "}
                venda(s) para chegar a 30%!
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={silenciar2h}
                  className="text-xs px-2 py-1 rounded bg-white border border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  Silenciar 2h
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- ATRASADAS --- */}
        {mostrarAtrasadas && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="fixed bottom-28 right-5 bg-white shadow-2xl rounded-xl border border-red-300 z-[9999] flex items-center space-x-4 p-4 max-w-sm"
          >
            <div className="w-16 h-16">
              <Lottie animationData={tristeAnimation} loop autoplay />
            </div>
            <div>
              <h2 className="text-lg font-bold text-red-600">
                ⚠ Tarefas Atrasadas!
              </h2>
              <p className="text-sm text-gray-700">
                Você tem{" "}
                <span className="font-bold text-red-700">{qtdAtrasadas}</span>{" "}
                lead(s) com tarefa(s) vencida(s).
                <br />
                Bora resolver isso agora mesmo!
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={silenciar2h}
                  className="text-xs px-2 py-1 rounded bg-white border border-red-300 text-red-700 hover:bg-red-50"
                >
                  Silenciar 2h
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- PARA HOJE --- */}
        {mostrarHoje && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="fixed bottom-52 right-5 bg-yellow-100 shadow-2xl rounded-xl border border-yellow-400 z-[9999] flex items-center space-x-4 p-4 max-w-sm"
          >
            <div className="w-16 h-16">
              <Lottie animationData={atencaoAnimation} loop autoplay />
            </div>
            <div>
              <h2 className="text-lg font-bold text-yellow-700">
                ⚠ Tarefas para Hoje!
              </h2>
              <p className="text-sm text-yellow-700">
                Você tem{" "}
                <span className="font-bold text-yellow-800">{qtdHoje}</span>{" "}
                lead(s) com tarefa(s) para hoje.
                <br />
                Não deixe para depois!
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={silenciar2h}
                  className="text-xs px-2 py-1 rounded bg-white border border-yellow-300 text-yellow-800 hover:bg-yellow-50"
                >
                  Silenciar 2h
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
